export const financialAdvisorsData = {
  industry: "Financial Advisors",
  slug: "financial-advisors",
  
  seo: {
    title: "Intake Forms for Financial Advisors | HeyIntake",
    description: "Qualify prospective clients and capture financial details with AI-powered intake forms. Know their investable assets, goals, risk tolerance, and timeline before the first meeting.",
    keywords: [
      "financial advisor intake form",
      "wealth management questionnaire",
      "investment client onboarding",
      "financial planning intake",
      "advisor prospect form",
      "client risk assessment form"
    ]
  },

  heroTitle: "Intake Forms for Financial Advisors",
  heroSubtitle: "Know their assets, goals, and risk tolerance before the first meeting. Qualify prospects, prepare personalized recommendations.",
  heroDescription: "Your time is best spent on clients who meet your minimums and genuinely need your expertise. HeyIntake helps you capture investable assets, financial goals, risk tolerance, and timeline so you can prioritize the right prospects.",
  
  whyAIScoring: {
    title: "Why Financial Advisors Need Smart Prospect Intake",
    intro: "Every meeting with an unqualified prospect is time away from serving your existing clients and growing your practice strategically.",
    paragraphs: [
      "Most advisor inquiries come with minimal context. 'I want to discuss my investments' could mean someone with $50,000 in an IRA or $5 million in complex assets. Without knowing their situation, you can't prepare relevant recommendations or even determine if they meet your client minimum.",
      "HeyIntake transforms your prospect qualification. Before any meeting, you know their investable assets, income level, financial goals, timeline, risk tolerance, and current advisor situation. Our AI identifies prospects who match your ideal client profile and flags potential compliance considerations.",
      "For RIAs, wealth managers, and financial planners, this means meetings with qualified prospects, personalized first conversations, and a practice full of clients you're excited to serve."
    ]
  },

  painPoints: [
    {
      title: "Unqualified Meetings",
      description: "Spending an hour with prospects who don't meet your asset minimum or need services you don't offer."
    },
    {
      title: "Unknown Financial Picture",
      description: "Walking into meetings blind, unable to prepare relevant recommendations."
    },
    {
      title: "Misaligned Expectations",
      description: "Prospects expecting day-trading tips when you specialize in retirement planning."
    },
    {
      title: "Compliance Concerns",
      description: "Discovering complex situations (trusts, business ownership) that require additional preparation."
    },
    {
      title: "Referral Follow-Up Gaps",
      description: "Warm referrals going cold because qualification takes too long."
    },
    {
      title: "Risk Tolerance Surprises",
      description: "Clients who said they're 'aggressive' panicking at the first downturn."
    }
  ],

  exampleFields: [
    {
      category: "Contact Information",
      fields: [
        "Full name",
        "Phone and email",
        "Occupation and employer",
        "Referral source",
        "Best time to contact"
      ]
    },
    {
      category: "Financial Snapshot",
      fields: [
        "Approximate investable assets",
        "Annual household income",
        "Current retirement accounts",
        "Other significant assets (real estate, business)",
        "Outstanding debts"
      ]
    },
    {
      category: "Goals & Timeline",
      fields: [
        "Primary financial goal",
        "Retirement timeline",
        "Major upcoming expenses",
        "Legacy/estate planning interests",
        "Risk tolerance self-assessment"
      ]
    },
    {
      category: "Current Situation",
      fields: [
        "Do you have a current financial advisor?",
        "Why are you considering a change?",
        "What services are you most interested in?",
        "Who else is involved in financial decisions?",
        "Any specific concerns or questions?"
      ]
    }
  ],

  exampleIntakeForm: {
    title: "Example Financial Advisor Intake Form",
    description: "Here's what a professional prospect intake looks like:",
    fields: [
      { label: "What prompted you to seek financial advice?", type: "select", options: ["Retirement planning", "Investment management", "Major life event", "Dissatisfied with current advisor", "Building wealth", "Tax planning", "Estate planning"] },
      { label: "Approximate investable assets", type: "select", options: ["$100K-$250K", "$250K-$500K", "$500K-$1M", "$1M-$5M", "$5M+"] },
      { label: "Annual household income", type: "select", options: ["$100K-$200K", "$200K-$350K", "$350K-$500K", "$500K-$1M", "$1M+"] },
      { label: "When do you plan to retire?", type: "select", options: ["Already retired", "Within 5 years", "5-10 years", "10-20 years", "20+ years"] },
      { label: "How would you describe your risk tolerance?", type: "select", options: ["Conservative", "Moderately conservative", "Moderate", "Moderately aggressive", "Aggressive"] },
      { label: "Do you currently work with a financial advisor?", type: "select", options: ["No", "Yes - looking to switch", "Yes - seeking second opinion"] },
      { label: "What's most important to you in an advisor?", type: "textarea" }
    ],
    cta: "Use this exact form → Start for $1"
  },

  howItWorks: {
    title: "How HeyIntake Works for Financial Advisors",
    subtitle: "From prospect inquiry to prepared meeting",
    steps: [
      {
        title: "Prospect Completes Intake",
        description: "Potential clients provide financial snapshot, goals, and preferences before any meeting."
      },
      {
        title: "AI Qualifies & Prepares",
        description: "Our AI identifies if they meet your minimums, flags complex situations, and helps prioritize."
      },
      {
        title: "Meet with Context",
        description: "Enter every meeting knowing their situation, ready to discuss relevant strategies."
      }
    ]
  },

  aiScoringExplanation: {
    title: "AI-Powered Prospect Qualification for Advisors",
    description: "HeyIntake's AI helps you identify ideal prospects and prepare for productive first meetings.",
    factors: [
      "Asset level vs. your client minimum",
      "Service needs alignment with your expertise",
      "Timeline and planning complexity",
      "Decision-maker involvement",
      "Reason for seeking new advisor"
    ],
    exampleLead: {
      name: "Dr. Sarah & James Mitchell",
      context: "$2.4M Assets • 8 Years to Retirement • Seeking New Advisor",
      summary: "Physician couple, $2.4M investable + practice sale pending. Retiring in 8 years. Current advisor not responsive. Interested in tax-efficient withdrawal strategies. Both spouses involved in decisions.",
      score: 96
    }
  },

  useCases: [
    {
      icon: "brain",
      title: "Pre-Qualify by Assets",
      description: "Know their investable assets before scheduling, ensuring they meet your minimum."
    },
    {
      icon: "file",
      title: "Assess Risk Tolerance",
      description: "Document risk tolerance upfront with standardized questions."
    },
    {
      icon: "clock",
      title: "Prioritize Hot Prospects",
      description: "AI identifies prospects actively looking to make a change vs. just browsing."
    },
    {
      icon: "users",
      title: "Understand the Full Picture",
      description: "Capture household income, assets, and goals before your first conversation."
    },
    {
      icon: "zap",
      title: "Automate Scheduling",
      description: "Qualified prospects can book directly into your calendar."
    },
    {
      icon: "chart",
      title: "Track Your Pipeline",
      description: "See all prospects from inquiry through onboarded client."
    }
  ],

  relatedIndustries: [
    { name: "Intake Forms for Accountants", path: "/intake-form-for-accountants" },
    { name: "Intake Forms for Lawyers", path: "/intake-form-for-lawyers" },
    { name: "Intake Forms for Consultants", path: "/intake-form-for-consultants" },
    { name: "Intake Forms for Coaches", path: "/intake-form-for-coaches" }
  ],

  ctaText: "Ready to Grow Your Practice Strategically?"
};
